import type { Match, Player, PlayerRecord, Round, Standing } from "./types";

/**
 * Returns the ID of the winner of the set, if it's completed. Returns null if match isn't done yet
 */
export function getMatchWinner(match: Match): number | null {
  if (match.player1Games === null || match.player2Games === null) return null;
  if (match.isBye) return match.player1;

  if (match.player1Games === match.player2Games) return null;
  return match.player1Games > match.player2Games ? match.player1 : match.player2;
}

/**
 * Returns if the given match has completed all of its sets
 */
export function isMatchReported(match: Match): boolean {
  return match.player1Games !== null && match.player2Games !== null;
}

/**
 * Returns if the given round has completed all of its matches
 */
export function isRoundComplete(round: Round): boolean {
  return round.matches.every(isMatchReported);
}

/**
 * Gets the PlayerRecord for each player in a map by PlayerID
 */
export function getRecords(rounds: Round[], players: Player[]): Map<number, PlayerRecord> {
  const records = new Map<number, PlayerRecord>();

  // Initialize records for all players
  for (const p of players) {
    records.set(p.id, {
      wins: 0,
      losses: 0,
      opponents: [],
      gameWins: 0,
      gameLosses: 0,
    })
  }

  for (const round of rounds) {
    for (const match of round.matches) {
      if (!isMatchReported(match)) continue;

      const winner = getMatchWinner(match);
      const p1Record = records.get(match.player1)!;

      if (match.isBye) {
        p1Record.wins++;
        continue;
      }

      const p2Record = records.get(match.player2!)!;

      p1Record.opponents.push(match.player2!);
      p2Record.opponents.push(match.player1);

      p1Record.gameWins += match.player1Games!;
      p1Record.gameLosses += match.player2Games!;
      p2Record.gameWins += match.player2Games!;
      p2Record.gameLosses += match.player1Games!;

      if (winner === match.player1) {
        p1Record.wins++;
        p2Record.losses++;
      } else {
        p2Record.wins++;
        p1Record.losses++;
      }
    }
  }

  return records;
}

/**
 * Returns the Buchholz score for a player — the sum of their opponents' win counts.
 * Higher means the player faced tougher competition.
 */
export function getBuchholzScore(playerID: number, records: Map<number, PlayerRecord>): number {
  const record = records.get(playerID);
  if (!record) return 0;

  return record.opponents.reduce((sum, oppID) => {
    const oppRecord = records.get(oppID);
    return sum + (oppRecord ? oppRecord.wins : 0);
  }, 0);
}

/**
 * Returns standings sorted by wins (desc), then Buchholz score (desc), then game differential (desc)
 */
export function getStandings(rounds: Round[], players: Player[]): Standing[] {
  const records = getRecords(rounds, players);

  const standings: Standing[] = players.map((player) => {
    const record = records.get(player.id)!;
    return {
      ...player,
      wins: record.wins,
      losses: record.losses,
      buchholzScore: getBuchholzScore(player.id, records),
      gameDiff: record.gameWins - record.gameLosses,
    };
  });

  standings.sort((a, b) => {
    if (b.wins !== a.wins) return b.wins - a.wins;
    if (b.buchholzScore !== a.buchholzScore) return b.buchholzScore - a.buchholzScore;
    return b.gameDiff - a.gameDiff;
  });

  return standings;
}

/**
 * Returns a set containing the IDs of all other players that the given player has previously played against
 */
export function getPreviousOpponents(rounds: Round[], playerID: number): Set<number> {
  const opponents = new Set<number>();

  for (const round of rounds) {
    for (const match of round.matches) {
      if (match.isBye) continue;
      if (match.player1 === playerID) opponents.add(match.player2!);
      if (match.player2 === playerID) opponents.add(match.player1);
    }
  }

  return opponents;
}

/**
 * Returns whether the given player has already had a bye
 */
export function hadBye(rounds: Round[], playerID: number): boolean {
  for (const round of rounds) {
    for (const match of round.matches) {
      if (match.isBye && match.player1 === playerID) {
        return true;
      }
    }
  }

  return false;
}
